import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { SidebarProvider, useSidebar } from "components/ui/Sidebar";
import Sidebar from "components/ui/Sidebar";
import Button from "components/ui/Button";
import StatCard from "components/ui/StatCard";
import Icon from "components/AppIcon";

import {
  getAccounts,
  getTrialBalance,
  getGeneralLedger,
  getAccountsReceivableAging,
  getProfitAndLoss,
  getBalanceSheet,
  getJournalEntries,
  getJournalEntryById,
  refreshOverdueInvoices,
} from "api/accounting.api";

import { QUERY_KEYS } from "config/queryKeys";
import { DEFAULT_STALE_TIME } from "config/constants";

const money = (value) =>
  Number(value || 0).toLocaleString("en-SA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const minorToMoney = (value) => Number(value || 0) / 100;

const getLineDebit = (line) =>
  Number(line.debit ?? line.debitAmount ?? minorToMoney(line.debitMinor));

const getLineCredit = (line) =>
  Number(line.credit ?? line.creditAmount ?? minorToMoney(line.creditMinor));

const getJournalLines = (entry) => entry.lines || entry.items || [];

const getJournalTotalDebit = (entry) =>
  Number(
    entry.totalDebit ??
      entry.debitTotal ??
      getJournalLines(entry).reduce((sum, line) => sum + getLineDebit(line), 0),
  );

const getJournalTotalCredit = (entry) =>
  Number(
    entry.totalCredit ??
      entry.creditTotal ??
      getJournalLines(entry).reduce(
        (sum, line) => sum + getLineCredit(line),
        0,
      ),
  );

const getEntryId = (entry) =>
  entry.journalEntryId ||
  entry.journalEntry?._id ||
  entry.journalEntry?.id ||
  entry.entryId ||
  entry.id ||
  entry._id;

const getLedgerEntries = (data) => {
  const directRows =
    data?.ledger || data?.entries || data?.journalEntries || data?.rows || [];
  const directRowList = Array.isArray(directRows) ? directRows : [];
  const hasNestedLines = directRowList.some(
    (entry) => getJournalLines(entry).length > 0,
  );

  if (directRowList.length > 0 && !hasNestedLines) {
    return directRowList;
  }

  const journalEntries = hasNestedLines
    ? directRowList
    : data?.journalEntries || data?.data || [];

  if (!Array.isArray(journalEntries)) {
    return [];
  }

  return journalEntries.flatMap((entry) =>
    getJournalLines(entry).map((line, index) => ({
      ...line,
      ledgerLineId:
        line.id || line._id || `${getEntryId(entry) || "entry"}-${index}`,
      journalEntryId: getEntryId(entry),
      date: entry.date || entry.entryDate,
      entryDate: entry.entryDate || entry.date,
      sourceModel: entry.sourceModel,
      entryType: entry.entryType,
      sourceType: entry.sourceType,
      sourceNumber: entry.sourceNumber,
      lineDescription: line.lineDescription || line.description || line.memo,
      description:
        line.lineDescription ||
        line.description ||
        line.memo ||
        entry.description,
      memo: line.memo || entry.memo,
      accountCode:
        line.accountCode || line.account?.code || line.accountId?.code,
      accountName:
        line.accountName || line.account?.name || line.accountId?.name,
      account: line.account || line.accountId,
      debit: getLineDebit(line),
      credit: getLineCredit(line),
    })),
  );
};

const csvEscape = (value) => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const downloadCsv = (filename, rows) => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) =>
      headers.map((header) => csvEscape(row[header])).join(","),
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
};

const buildExportRows = (reportType, data) => {
  if (reportType === "accounts") {
    const rows = data?.accounts || data?.data || [];

    return rows.map((account) => ({
      Code: account.code || "",
      Name: account.name || "",
      Type: account.type || "",
      NormalBalance: account.normalBalance || "",
      SystemKey: account.systemKey || "",
      IsActive: account.isActive === false ? "No" : "Yes",
    }));
  }

  if (reportType === "trialBalance") {
    const rows = data?.rows || [];

    return rows.map((row) => ({
      AccountCode: row.code,
      AccountName: row.name,
      Type: row.type,
      Debit: row.debitBalance || 0,
      Credit: row.creditBalance || 0,
    }));
  }

  if (reportType === "generalLedger") {
    const rows = getLedgerEntries(data);

    return rows.map((entry) => ({
      Date: entry.date || entry.entryDate || "",
      AccountCode: entry.accountCode || entry.account?.code || "",
      AccountName: entry.accountName || entry.account?.name || "",
      SourceType:
        entry.sourceModel || entry.entryType || entry.sourceType || "",
      SourceNumber: entry.sourceNumber || "",
      Description:
        entry.lineDescription || entry.description || entry.memo || "",
      Debit: getLineDebit(entry),
      Credit: getLineCredit(entry),
    }));
  }

  if (reportType === "journalEntries") {
    const rows = data?.journalEntries || data?.entries || data?.data || [];

    return rows.map((entry) => ({
      Date: entry.entryDate || entry.date || "",
      EntryNumber: entry.entryNumber || entry.number || "",
      SourceType:
        entry.sourceModel || entry.entryType || entry.sourceType || "",
      SourceNumber: entry.sourceNumber || "",
      Description: entry.description || "",
      TotalDebit: getJournalTotalDebit(entry),
      TotalCredit: getJournalTotalCredit(entry),
      Status: entry.status || "",
    }));
  }

  if (reportType === "arAging") {
    const rows = data?.invoices || [];

    return rows.map((invoice) => ({
      InvoiceNumber: invoice.invoiceNumber,
      ClientName: invoice.clientName,
      DueDate: invoice.dueDate || "",
      BalanceDue: invoice.balanceDue || 0,
      Bucket: invoice.bucket || "",
    }));
  }

  if (reportType === "profitAndLoss") {
    const summary = data?.summary || {};

    return [
      {
        NetRevenue: summary.netRevenue || 0,
        TotalExpenses: summary.totalExpenses || 0,
        NetIncome: summary.netIncome || 0,
        ContraRevenue: summary.contraRevenue || 0,
      },
    ];
  }

  if (reportType === "balanceSheet") {
    const summary = data?.summary || {};

    return [
      {
        TotalAssets: summary.totalAssets || 0,
        TotalLiabilities: summary.totalLiabilities || 0,
        EquityWithCurrentEarnings: summary.totalEquityWithCurrentEarnings || 0,
        Difference: summary.difference || 0,
        IsBalanced: summary.isBalanced ? "Yes" : "No",
      },
    ];
  }

  return [];
};

const reportFileName = (reportType, filters) => {
  const names = {
    accounts: "chart-of-accounts",
    trialBalance: "trial-balance",
    generalLedger: "general-ledger",
    journalEntries: "journal-entries",
    arAging: "ar-aging",
    profitAndLoss: "profit-and-loss",
    balanceSheet: "balance-sheet",
  };

  const datePart =
    reportType === "accounts" ||
    reportType === "trialBalance" ||
    reportType === "arAging" ||
    reportType === "balanceSheet"
      ? `as-of-${filters.asOfDate}`
      : `${filters.startDate}-to-${filters.endDate}`;

  return `${names[reportType] || "accounting-report"}-${datePart}.csv`;
};

const today = new Date().toISOString().slice(0, 10);

const firstDayOfMonth = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};

const ReportCard = ({ title, description, icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left rounded-xl border p-4 transition-all ${
      active
        ? "border-primary bg-primary/10"
        : "border-border bg-card hover:bg-muted/50"
    }`}
  >
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon name={icon} size={18} className="text-primary" />
      </div>
      <div className="font-semibold text-foreground">{title}</div>
    </div>
    <p className="text-sm text-muted-foreground">{description}</p>
  </button>
);

const TrialBalanceView = ({ data }) => {
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Trial Balance</h3>
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            totals.isBalanced
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {totals.isBalanced ? "Balanced" : "Not Balanced"}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold">
                Account
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold">
                Type
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold">
                Debit
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold">
                Credit
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <tr key={row.accountId}>
                <td className="px-4 py-3 text-sm">
                  <div className="font-medium">
                    {row.code} — {row.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {row.type}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  {row.debitBalance ? `SR ${money(row.debitBalance)}` : "—"}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  {row.creditBalance ? `SR ${money(row.creditBalance)}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-muted">
            <tr>
              <td colSpan={2} className="px-4 py-3 text-sm font-semibold">
                Total
              </td>
              <td className="px-4 py-3 text-sm text-right font-semibold">
                SR {money(totals.debitBalance)}
              </td>
              <td className="px-4 py-3 text-sm text-right font-semibold">
                SR {money(totals.creditBalance)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const GeneralLedgerView = ({ data, onViewEntry }) => {
  const entries = getLedgerEntries(data);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">General Ledger</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Posted journal lines by account.
        </p>
      </div>

      {entries.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          No ledger entries found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Account
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Description
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold">
                  Debit
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold">
                  Credit
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border">
              {entries.map((entry, index) => (
                <tr key={entry.ledgerLineId || entry.id || entry._id || index}>
                  <td className="px-4 py-3 text-sm">
                    {entry.date || entry.entryDate
                      ? new Date(
                          entry.date || entry.entryDate,
                        ).toLocaleDateString("en-SA")
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {entry.accountCode || entry.account?.code || "—"}{" "}
                    {entry.accountName || entry.account?.name || ""}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {entry.sourceModel || entry.entryType || "—"}
                    {entry.sourceNumber ? ` / ${entry.sourceNumber}` : ""}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground max-w-[260px] truncate">
                    {entry.lineDescription ||
                      entry.description ||
                      entry.memo ||
                      "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {getLineDebit(entry)
                      ? `SR ${money(getLineDebit(entry))}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {getLineCredit(entry)
                      ? `SR ${money(getLineCredit(entry))}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {getEntryId(entry) ? (
                      <button
                        type="button"
                        onClick={() => onViewEntry(getEntryId(entry))}
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <Icon name="Eye" size={14} />
                        View
                      </button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const JournalEntriesView = ({ data, onViewEntry }) => {
  const entries = data?.journalEntries || data?.entries || data?.data || [];

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Journal Entries</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Source-generated accounting entries.
        </p>
      </div>

      {entries.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          No journal entries found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Entry No.
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Description
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold">
                  Debit
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold">
                  Credit
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border">
              {entries.map((entry) => (
                <tr key={entry.id || entry._id}>
                  <td className="px-4 py-3 text-sm">
                    {entry.entryDate || entry.date
                      ? new Date(
                          entry.entryDate || entry.date,
                        ).toLocaleDateString("en-SA")
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {entry.entryNumber || entry.number || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {entry.sourceModel || entry.entryType || "—"}
                    {entry.sourceNumber ? ` / ${entry.sourceNumber}` : ""}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground max-w-[260px] truncate">
                    {entry.description || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    SR {money(getJournalTotalDebit(entry))}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    SR {money(getJournalTotalCredit(entry))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        entry.status === "POSTED"
                          ? "bg-green-100 text-green-700"
                          : entry.status === "REVERSED"
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {entry.status || "Posted"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      type="button"
                      onClick={() => onViewEntry(entry.id || entry._id)}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <Icon name="Eye" size={14} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const JournalEntryDetailModal = ({ entryId, onClose }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEYS.accounting.journalEntry(entryId),
    queryFn: () => getJournalEntryById(entryId),
    enabled: !!entryId,
    staleTime: DEFAULT_STALE_TIME,
  });

  const entry = data?.journalEntry || data?.entry || data?.data || data || {};
  const lines = getJournalLines(entry);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 16 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Journal Entry Detail
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {entry.entryNumber || entry.number || "Loading..."}
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="rounded-xl border border-border p-8 text-center text-muted-foreground">
              Loading journal entry...
            </div>
          ) : isError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/10 p-4 text-sm text-red-700 dark:text-red-300">
              Failed to load journal entry.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="text-xs text-muted-foreground mb-1">
                    Entry Date
                  </div>
                  <div className="font-semibold text-foreground">
                    {entry.entryDate || entry.date
                      ? new Date(
                          entry.entryDate || entry.date,
                        ).toLocaleDateString("en-SA")
                      : "—"}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="text-xs text-muted-foreground mb-1">
                    Total Debit
                  </div>
                  <div className="font-semibold text-foreground">
                    SR {money(getJournalTotalDebit(entry))}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="text-xs text-muted-foreground mb-1">
                    Total Credit
                  </div>
                  <div className="font-semibold text-foreground">
                    SR {money(getJournalTotalCredit(entry))}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Source:</span>{" "}
                    <span className="font-medium text-foreground">
                      {entry.sourceModel || entry.entryType || "—"}
                      {entry.sourceNumber ? ` / ${entry.sourceNumber}` : ""}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>{" "}
                    <span className="font-medium text-foreground">
                      {entry.status || "POSTED"}
                    </span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-muted-foreground">Description:</span>{" "}
                    <span className="font-medium text-foreground">
                      {entry.description || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border overflow-hidden">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold text-foreground">
                    Debit / Credit Lines
                  </h3>
                </div>

                {lines.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No journal lines found.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold">
                            Account
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">
                            Description
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold">
                            Debit
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold">
                            Credit
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-border">
                        {lines.map((line, index) => (
                          <tr key={line.id || line._id || index}>
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium text-foreground">
                                {line.accountCode ||
                                  line.account?.code ||
                                  line.accountId?.code ||
                                  "—"}{" "}
                                {line.accountName ||
                                  line.account?.name ||
                                  line.accountId?.name ||
                                  ""}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {line.description || line.memo || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {getLineDebit(line)
                                ? `SR ${money(getLineDebit(line))}`
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {getLineCredit(line)
                                ? `SR ${money(getLineCredit(line))}`
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex justify-end">
          <Button variant="outline" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ChartOfAccountsView = ({ data }) => {
  const accounts = data?.accounts || data?.data || [];

  const typeColor = {
    ASSET: "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
    LIABILITY:
      "bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300",
    EQUITY:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300",
    INCOME:
      "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300",
    EXPENSE: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300",
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Chart of Accounts</h3>
        <p className="text-sm text-muted-foreground mt-1">
          System accounts used for invoices, payments, tax, receivables, and
          reporting.
        </p>
      </div>

      {accounts.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          No accounts found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Normal Balance
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  System Key
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Status
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border">
              {accounts.map((account) => (
                <tr key={account.id || account._id}>
                  <td className="px-4 py-3 text-sm font-mono text-foreground">
                    {account.code || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-foreground">
                      {account.name || "Unnamed Account"}
                    </div>
                    {account.description && (
                      <div className="text-xs text-muted-foreground mt-0.5 max-w-[320px] truncate">
                        {account.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        typeColor[account.type] ||
                        "bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-300"
                      }`}
                    >
                      {account.type || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {account.normalBalance || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-muted-foreground">
                    {account.systemKey || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        account.isActive === false
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {account.isActive === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ArAgingView = ({ data }) => {
  const summary = data?.summary || {};
  const invoices = data?.invoices || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ["Current", summary.current],
          ["1–30", summary.days1To30],
          ["31–60", summary.days31To60],
          ["61–90", summary.days61To90],
          ["90+", summary.over90],
          ["Total", summary.totalReceivable],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-xl border border-border bg-card p-4"
          >
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="font-semibold text-foreground">
              SR {money(value)}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">AR Aging</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Invoice
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Client
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold">
                  Balance
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold">
                  Bucket
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.map((invoice) => (
                <tr key={invoice.invoiceId}>
                  <td className="px-4 py-3 text-sm">{invoice.invoiceNumber}</td>
                  <td className="px-4 py-3 text-sm">{invoice.clientName}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    SR {money(invoice.balanceDue)}
                  </td>
                  <td className="px-4 py-3 text-sm">{invoice.bucket}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ProfitAndLossView = ({ data }) => {
  const summary = data?.summary || {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <StatCard
        label="Net Revenue"
        value={`SR ${money(summary.netRevenue)}`}
        icon="TrendingUp"
        color="text-green-600 bg-green-100"
      />
      <StatCard
        label="Total Expenses"
        value={`SR ${money(summary.totalExpenses)}`}
        icon="Receipt"
        color="text-orange-600 bg-orange-100"
      />
      <StatCard
        label="Net Income"
        value={`SR ${money(summary.netIncome)}`}
        icon="LineChart"
        color="text-primary bg-primary/10"
      />
      <StatCard
        label="Contra Revenue"
        value={`SR ${money(summary.contraRevenue)}`}
        icon="RotateCcw"
        color="text-purple-600 bg-purple-100"
      />
    </div>
  );
};

const BalanceSheetView = ({ data }) => {
  const summary = data?.summary || {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <StatCard
        label="Total Assets"
        value={`SR ${money(summary.totalAssets)}`}
        icon="Landmark"
        color="text-blue-600 bg-blue-100"
      />
      <StatCard
        label="Total Liabilities"
        value={`SR ${money(summary.totalLiabilities)}`}
        icon="Scale"
        color="text-orange-600 bg-orange-100"
      />
      <StatCard
        label="Equity + Earnings"
        value={`SR ${money(summary.totalEquityWithCurrentEarnings)}`}
        icon="BadgeDollarSign"
        color="text-green-600 bg-green-100"
      />
      <StatCard
        label="Difference"
        value={`SR ${money(summary.difference)}`}
        icon={summary.isBalanced ? "CheckCircle2" : "AlertTriangle"}
        color={
          summary.isBalanced
            ? "text-green-600 bg-green-100"
            : "text-red-600 bg-red-100"
        }
      />
    </div>
  );
};

const AccountingContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const queryClient = useQueryClient();
  const [activeReport, setActiveReport] = useState("trialBalance");
  const [selectedJournalEntryId, setSelectedJournalEntryId] = useState(null);
  const [filters, setFilters] = useState({
    startDate: firstDayOfMonth(),
    endDate: today,
    asOfDate: today,
  });

  const periodParams = {
    startDate: filters.startDate,
    endDate: filters.endDate,
  };

  const asOfParams = {
    asOfDate: filters.asOfDate,
  };

  const accountsQuery = useQuery({
    queryKey: QUERY_KEYS.accounting.accounts(),
    queryFn: () => getAccounts(),
    staleTime: DEFAULT_STALE_TIME,
  });

  const trialBalanceQuery = useQuery({
    queryKey: QUERY_KEYS.accounting.trialBalance(asOfParams),
    queryFn: () => getTrialBalance(asOfParams),
    staleTime: DEFAULT_STALE_TIME,
  });

  const generalLedgerQuery = useQuery({
    queryKey: QUERY_KEYS.accounting.generalLedger(periodParams),
    queryFn: () => getGeneralLedger(periodParams),
    staleTime: DEFAULT_STALE_TIME,
  });

  const journalEntriesQuery = useQuery({
    queryKey: QUERY_KEYS.accounting.journalEntries(periodParams),
    queryFn: () => getJournalEntries(periodParams),
    staleTime: DEFAULT_STALE_TIME,
  });

  const arAgingQuery = useQuery({
    queryKey: QUERY_KEYS.accounting.arAging(asOfParams),
    queryFn: () => getAccountsReceivableAging(asOfParams),
    staleTime: DEFAULT_STALE_TIME,
  });

  const profitAndLossQuery = useQuery({
    queryKey: QUERY_KEYS.accounting.profitAndLoss(periodParams),
    queryFn: () => getProfitAndLoss(periodParams),
    staleTime: DEFAULT_STALE_TIME,
  });

  const balanceSheetQuery = useQuery({
    queryKey: QUERY_KEYS.accounting.balanceSheet(asOfParams),
    queryFn: () => getBalanceSheet(asOfParams),
    staleTime: DEFAULT_STALE_TIME,
  });

  const refreshOverdueMutation = useMutation({
    mutationFn: () => refreshOverdueInvoices({ asOfDate: filters.asOfDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.accounting.arAging(asOfParams),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.invoices.list(),
      });
    },
  });

  const activeData = {
    accounts: accountsQuery.data,
    trialBalance: trialBalanceQuery.data,
    generalLedger: generalLedgerQuery.data,
    journalEntries: journalEntriesQuery.data,
    arAging: arAgingQuery.data,
    profitAndLoss: profitAndLossQuery.data,
    balanceSheet: balanceSheetQuery.data,
  }[activeReport];

  const isLoading = {
    accounts: accountsQuery.isLoading,
    trialBalance: trialBalanceQuery.isLoading,
    generalLedger: generalLedgerQuery.isLoading,
    journalEntries: journalEntriesQuery.isLoading,
    arAging: arAgingQuery.isLoading,
    profitAndLoss: profitAndLossQuery.isLoading,
    balanceSheet: balanceSheetQuery.isLoading,
  }[activeReport];

  const handleExportCsv = () => {
    const rows = buildExportRows(activeReport, activeData);

    if (!rows.length) return;

    downloadCsv(reportFileName(activeReport, filters), rows);
  };

  const handleRefreshOverdue = async () => {
    try {
      await refreshOverdueMutation.mutateAsync();
    } catch (error) {
      console.error("Failed to refresh overdue invoices", error);
    }
  };

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <main className="p-4 md:p-6">
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("accounting.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("accounting.description")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Period: {filters.startDate} to {filters.endDate} · As of:{" "}
                {filters.asOfDate}
              </p>
            </div>

            <div className="flex gap-2">
              {activeReport === "arAging" && (
                <Button
                  variant="outline"
                  size="md"
                  iconName="RefreshCw"
                  disabled={refreshOverdueMutation.isPending}
                  onClick={handleRefreshOverdue}
                >
                  {refreshOverdueMutation.isPending
                    ? t("accounting.refreshing")
                    : t("accounting.refreshOverdue")}
                </Button>
              )}

              <Button
                variant="outline"
                size="md"
                iconName="Download"
                disabled={isLoading}
                onClick={handleExportCsv}
              >
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("accounting.startDate")}
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    startDate: event.target.value,
                  }))
                }
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("accounting.endDate")}
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    endDate: event.target.value,
                  }))
                }
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t("accounting.asOfDate")}
              </label>
              <input
                type="date"
                value={filters.asOfDate}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    asOfDate: event.target.value,
                  }))
                }
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              />
            </div>

            <div className="text-sm text-muted-foreground lg:pb-2">
              {t("accounting.balanceReportsHint")}
            </div>
          </div>
        </div>

        {refreshOverdueMutation.isSuccess && activeReport === "arAging" && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {t("accounting.overdueRefreshed")}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-4 mb-6">
          <ReportCard
            title={t("accounting.reports.accounts.title")}
            description={t("accounting.reports.accounts.description")}
            icon="ListTree"
            active={activeReport === "accounts"}
            onClick={() => setActiveReport("accounts")}
          />
          <ReportCard
            title={t("accounting.reports.trialBalance.title")}
            description={t("accounting.reports.trialBalance.description")}
            icon="Scale"
            active={activeReport === "trialBalance"}
            onClick={() => setActiveReport("trialBalance")}
          />
          <ReportCard
            title={t("accounting.reports.generalLedger.title")}
            description={t("accounting.reports.generalLedger.description")}
            icon="BookOpen"
            active={activeReport === "generalLedger"}
            onClick={() => setActiveReport("generalLedger")}
          />

          <ReportCard
            title={t("accounting.reports.journalEntries.title")}
            description={t("accounting.reports.journalEntries.description")}
            icon="ListChecks"
            active={activeReport === "journalEntries"}
            onClick={() => setActiveReport("journalEntries")}
          />
          <ReportCard
            title={t("accounting.reports.arAging.title")}
            description={t("accounting.reports.arAging.description")}
            icon="Clock"
            active={activeReport === "arAging"}
            onClick={() => setActiveReport("arAging")}
          />
          <ReportCard
            title={t("accounting.reports.profitAndLoss.title")}
            description={t("accounting.reports.profitAndLoss.description")}
            icon="LineChart"
            active={activeReport === "profitAndLoss"}
            onClick={() => setActiveReport("profitAndLoss")}
          />
          <ReportCard
            title={t("accounting.reports.balanceSheet.title")}
            description={t("accounting.reports.balanceSheet.description")}
            icon="Landmark"
            active={activeReport === "balanceSheet"}
            onClick={() => setActiveReport("balanceSheet")}
          />
        </div>

        {isLoading ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
            {t("accounting.loadingReport")}
          </div>
        ) : (
          <>
            {activeReport === "accounts" && (
              <ChartOfAccountsView data={activeData} />
            )}
            {activeReport === "trialBalance" && (
              <TrialBalanceView data={activeData} />
            )}
            {activeReport === "generalLedger" && (
              <GeneralLedgerView
                data={activeData}
                onViewEntry={setSelectedJournalEntryId}
              />
            )}
            {activeReport === "journalEntries" && (
              <JournalEntriesView
                data={activeData}
                onViewEntry={setSelectedJournalEntryId}
              />
            )}
            {activeReport === "arAging" && <ArAgingView data={activeData} />}
            {activeReport === "profitAndLoss" && (
              <ProfitAndLossView data={activeData} />
            )}
            {activeReport === "balanceSheet" && (
              <BalanceSheetView data={activeData} />
            )}
          </>
        )}

        <AnimatePresence>
          {selectedJournalEntryId && (
            <JournalEntryDetailModal
              entryId={selectedJournalEntryId}
              onClose={() => setSelectedJournalEntryId(null)}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

const AccountingPage = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <AccountingContent />
    </div>
  </SidebarProvider>
);

export default AccountingPage;
